import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Building2,
  FileText,
  Send,
  CheckCircle,
  Clock,
  AlertTriangle,
  Upload,
  Trash2,
  Eye,
  RefreshCw,
  MessageSquare,
  Lock,
  ArrowRight,
  ArrowLeft,
  XCircle,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Use relative URL - works with any domain
const API_URL = '/api';

// Estados del onboarding
const ESTADOS = {
  PENDIENTE: { label: 'Pendiente', color: 'bg-slate-100 text-slate-700', icon: Clock },
  EN_PROGRESO: { label: 'En Progreso', color: 'bg-blue-100 text-blue-700', icon: RefreshCw },
  ENVIADO: { label: 'Enviado', color: 'bg-purple-100 text-purple-700', icon: Send },
  EN_REVISION: { label: 'En Revisión', color: 'bg-amber-100 text-amber-700', icon: Eye },
  INFO_ADICIONAL: { label: 'Info Adicional', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
  APROBADO: { label: 'Aprobado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  RECHAZADO: { label: 'Rechazado', color: 'bg-red-100 text-red-700', icon: XCircle },
};

// Provincias de Argentina
const PROVINCIAS = [
  'Buenos Aires', 'CABA', 'Catamarca', 'Chaco', 'Chubut', 'Córdoba', 'Corrientes',
  'Entre Ríos', 'Formosa', 'Jujuy', 'La Pampa', 'La Rioja', 'Mendoza', 'Misiones',
  'Neuquén', 'Río Negro', 'Salta', 'San Juan', 'San Luis', 'Santa Cruz', 'Santa Fe',
  'Santiago del Estero', 'Tierra del Fuego', 'Tucumán'
];

export default function OnboardingPortal() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Request state
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [portalInfo, setPortalInfo] = useState(null);
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  
  // Form states
  const [empresaForm, setEmpresaForm] = useState({
    razon_social: '',
    cuit: '',
    domicilio: '',
    localidad: '',
    provincia: '',
    codigo_postal: '',
    telefono: '',
    email: '',
    actividad_principal: '',
    fecha_constitucion: ''
  });
  
  // Estado para límites eliminado - ya no se requiere en el onboarding simplificado
  
  // Dialogs
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showRequestPasswordDialog, setShowRequestPasswordDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newComment, setNewComment] = useState('');

  // Load portal info on mount
  useEffect(() => {
    loadPortalInfo();
  }, [token]);

  // Auto-save effect
  useEffect(() => {
    if (isAuthenticated && request && currentStep < 2) {
      const timer = setTimeout(() => {
        handleAutoSave();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [empresaForm]);

  const loadPortalInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/onboarding/portal/${token}`);
      setPortalInfo(res.data);
      
      // Si ya está autenticado (tiene datos en sessionStorage), cargar request completo
      const savedAuth = sessionStorage.getItem(`onboarding_${token}`);
      if (savedAuth) {
        await loadFullRequest();
        setIsAuthenticated(true);
      }
    } catch (error) {
      toast.error('Enlace inválido o expirado');
    } finally {
      setLoading(false);
    }
  };

  const loadFullRequest = async () => {
    try {
      const res = await axios.post(`${API_URL}/onboarding/portal/${token}/login`, loginData);
      setRequest(res.data);
      populateForms(res.data);
      return res.data;
    } catch (error) {
      // Si falla, intentar solo obtener datos básicos
      console.error('Error loading full request:', error);
    }
  };

  const populateForms = (data) => {
    if (data.empresa_data) {
      setEmpresaForm(data.empresa_data);
    }
    // Límites eliminados del onboarding simplificado
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    
    try {
      const res = await axios.post(`${API_URL}/onboarding/portal/${token}/login`, loginData);
      setRequest(res.data);
      populateForms(res.data);
      setIsAuthenticated(true);
      sessionStorage.setItem(`onboarding_${token}`, 'true');
      toast.success('Bienvenido');
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleAutoSave = useCallback(async () => {
    if (!isAuthenticated || !request) return;
    
    try {
      if (currentStep === 0) {
        await axios.put(`${API_URL}/onboarding/portal/${token}/empresa`, empresaForm);
      }
    } catch (error) {
      console.error('Auto-save error:', error);
    }
  }, [currentStep, empresaForm, isAuthenticated, request, token]);

  const handleSaveEmpresa = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/onboarding/portal/${token}/empresa`, empresaForm);
      setRequest(res.data);
      toast.success('Datos guardados');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async (tipoDocumentoId, file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('tipo_documento_id', tipoDocumentoId);
      formData.append('file', file);
      
      const res = await axios.post(
        `${API_URL}/onboarding/portal/${token}/documents`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setRequest(res.data);
      toast.success('Documento subido');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      const res = await axios.delete(`${API_URL}/onboarding/portal/${token}/documents/${documentId}`);
      setRequest(res.data);
      toast.success('Documento eliminado');
    } catch (error) {
      toast.error('Error al eliminar documento');
    }
  };

  const handleSubmit = async () => {
    try {
      const res = await axios.post(`${API_URL}/onboarding/portal/${token}/submit`);
      setRequest(res.data);
      setShowSubmitDialog(false);
      toast.success(
        '¡Solicitud enviada a revisión! Nuestro equipo la revisará y te notificaremos por email cuando esté aprobada.',
        { duration: 6000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar la solicitud');
    }
  };

  const handleRequestNewPassword = async () => {
    try {
      await axios.post(`${API_URL}/onboarding/portal/${token}/request-password`);
      setShowRequestPasswordDialog(false);
      toast.success('Solicitud enviada. Le notificaremos cuando sea aprobada.');
    } catch (error) {
      toast.error('Error al solicitar nueva contraseña');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      await axios.post(`${API_URL}/onboarding/portal/${token}/comment`, {
        message: newComment,
        is_internal: false
      });
      setNewComment('');
      // Recargar request para ver el comentario
      const res = await axios.post(`${API_URL}/onboarding/portal/${token}/login`, loginData);
      setRequest(res.data);
      toast.success('Comentario enviado');
    } catch (error) {
      toast.error('Error al enviar comentario');
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(num);
  };

  const getProgress = () => {
    if (!request?.progreso) return 0;
    const { datos_empresa, documentos, revision } = request.progreso;
    let completed = 0;
    if (datos_empresa) completed += 33;
    if (documentos) completed += 33;
    if (revision) completed += 34;
    return completed;
  };

  const steps = [
    { key: 'datos', label: 'Datos de Empresa', icon: Building2 },
    { key: 'documentos', label: 'Documentos', icon: FileText },
    { key: 'revision', label: 'Enviar', icon: Send },
  ];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <RefreshCw className="animate-spin text-blue-600" size={40} />
      </div>
    );
  }

  // Invalid token
  if (!portalInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="mx-auto text-red-500 mb-4" size={48} />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Enlace Inválido</h2>
            <p className="text-slate-500">Este enlace no es válido o ha expirado.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
              <Building2 className="text-white" size={32} />
            </div>
            <CardTitle className="text-2xl">Bienvenido a Waya Empresas</CardTitle>
            <p className="text-slate-500 mt-2">
              Trámite para: <strong>{portalInfo.empresa_nombre}</strong>
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="su@email.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Contraseña</Label>
                <Input
                  type="password"
                  placeholder="Contraseña temporal"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                />
              </div>
              
              {loginError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {loginError}
                </div>
              )}
              
              {portalInfo.password_expired && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm flex items-center gap-2">
                  <AlertTriangle size={16} />
                  Su contraseña ha expirado.
                  <button
                    type="button"
                    className="underline font-medium"
                    onClick={() => setShowRequestPasswordDialog(true)}
                  >
                    Solicitar nueva
                  </button>
                </div>
              )}
              
              <Button type="submit" className="w-full" disabled={loginLoading}>
                {loginLoading ? (
                  <><RefreshCw className="animate-spin mr-2" size={16} /> Ingresando...</>
                ) : (
                  <><Lock className="mr-2" size={16} /> Ingresar</>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
        
        {/* Dialog solicitar contraseña */}
        <Dialog open={showRequestPasswordDialog} onOpenChange={setShowRequestPasswordDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Solicitar Nueva Contraseña</DialogTitle>
              <DialogDescription>
                Se enviará una solicitud que será revisada por nuestro equipo. 
                Recibirá un email cuando sea aprobada.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRequestPasswordDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleRequestNewPassword}>
                Solicitar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main portal - already authenticated
  const estadoInfo = ESTADOS[request?.estado] || ESTADOS.PENDIENTE;
  const EstadoIcon = estadoInfo.icon;
  const isReadOnly = ['ENVIADO', 'EN_REVISION', 'APROBADO', 'RECHAZADO'].includes(request?.estado);
  const representantePadron = request?.representante_padron;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Building2 className="text-white" size={20} />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">Waya Empresas</h1>
              <p className="text-sm text-slate-500">{request?.empresa_data?.razon_social || request?.empresa_nombre_inicial}</p>
            </div>
          </div>
          <Badge className={estadoInfo.color}>
            <EstadoIcon size={14} className="mr-1" />
            {estadoInfo.label}
          </Badge>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Datos del Representante Legal (desde Padrón) */}
        {representantePadron && (
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="text-green-600" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-green-800 mb-2">Representante Legal Verificado</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-green-600 text-xs">Nombre</span>
                      <p className="font-medium text-green-900">{representantePadron.nombre} {representantePadron.apellido}</p>
                    </div>
                    <div>
                      <span className="text-green-600 text-xs">CUIT</span>
                      <p className="font-medium text-green-900">{representantePadron.cuit}</p>
                    </div>
                    {representantePadron.dni && (
                      <div>
                        <span className="text-green-600 text-xs">DNI</span>
                        <p className="font-medium text-green-900">{representantePadron.dni}</p>
                      </div>
                    )}
                    {representantePadron.mail && (
                      <div>
                        <span className="text-green-600 text-xs">Email</span>
                        <p className="font-medium text-green-900">{representantePadron.mail}</p>
                      </div>
                    )}
                    {representantePadron.telefono && (
                      <div>
                        <span className="text-green-600 text-xs">Teléfono</span>
                        <p className="font-medium text-green-900">{representantePadron.telefono}</p>
                      </div>
                    )}
                    {representantePadron.domicilio && (
                      <div className="col-span-2">
                        <span className="text-green-600 text-xs">Domicilio</span>
                        <p className="font-medium text-green-900">
                          {representantePadron.domicilio}
                          {representantePadron.localidad && `, ${representantePadron.localidad}`}
                          {representantePadron.provincia && `, ${representantePadron.provincia}`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-slate-700">Progreso del trámite</span>
              <span className="text-sm text-slate-500">{getProgress()}% completado</span>
            </div>
            <Progress value={getProgress()} className="h-2" />
            
            {/* Steps */}
            <div className="flex justify-between mt-6">
              {steps.map((step, idx) => {
                const StepIcon = step.icon;
                const isComplete = request?.progreso?.[step.key === 'datos' ? 'datos_empresa' : step.key];
                const isCurrent = currentStep === idx;
                
                return (
                  <button
                    key={step.key}
                    onClick={() => !isReadOnly && setCurrentStep(idx)}
                    disabled={isReadOnly}
                    className={`flex flex-col items-center gap-2 p-2 rounded-lg transition-colors ${
                      isCurrent ? 'bg-blue-50' : ''
                    } ${isReadOnly ? 'cursor-default' : 'cursor-pointer hover:bg-slate-50'}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isComplete ? 'bg-green-100 text-green-700' :
                      isCurrent ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-400'
                    }`}>
                      {isComplete ? <CheckCircle size={20} /> : <StepIcon size={20} />}
                    </div>
                    <span className={`text-xs font-medium ${isCurrent ? 'text-blue-700' : 'text-slate-500'}`}>
                      {step.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Content based on current step */}
        {currentStep === 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 size={20} className="text-blue-600" />
                Datos de la Empresa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Razón Social *</Label>
                  <Input
                    value={empresaForm.razon_social}
                    onChange={(e) => setEmpresaForm({ ...empresaForm, razon_social: e.target.value })}
                    disabled={isReadOnly}
                    placeholder="Nombre de la empresa"
                  />
                </div>
                <div>
                  <Label>CUIT *</Label>
                  <Input
                    value={empresaForm.cuit}
                    onChange={(e) => setEmpresaForm({ ...empresaForm, cuit: e.target.value })}
                    disabled={isReadOnly}
                    placeholder="XX-XXXXXXXX-X"
                  />
                </div>
              </div>
              
              <div>
                <Label>Domicilio *</Label>
                <Input
                  value={empresaForm.domicilio}
                  onChange={(e) => setEmpresaForm({ ...empresaForm, domicilio: e.target.value })}
                  disabled={isReadOnly}
                  placeholder="Calle y número"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Localidad *</Label>
                  <Input
                    value={empresaForm.localidad}
                    onChange={(e) => setEmpresaForm({ ...empresaForm, localidad: e.target.value })}
                    disabled={isReadOnly}
                  />
                </div>
                <div>
                  <Label>Provincia *</Label>
                  <select
                    className="w-full h-10 px-3 border rounded-md bg-white"
                    value={empresaForm.provincia}
                    onChange={(e) => setEmpresaForm({ ...empresaForm, provincia: e.target.value })}
                    disabled={isReadOnly}
                  >
                    <option value="">Seleccionar...</option>
                    {PROVINCIAS.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Código Postal</Label>
                  <Input
                    value={empresaForm.codigo_postal}
                    onChange={(e) => setEmpresaForm({ ...empresaForm, codigo_postal: e.target.value })}
                    disabled={isReadOnly}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Teléfono</Label>
                  <Input
                    value={empresaForm.telefono}
                    onChange={(e) => setEmpresaForm({ ...empresaForm, telefono: e.target.value })}
                    disabled={isReadOnly}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={empresaForm.email}
                    onChange={(e) => setEmpresaForm({ ...empresaForm, email: e.target.value })}
                    disabled={isReadOnly}
                  />
                </div>
              </div>
              
              <div>
                <Label>Actividad Principal</Label>
                <Input
                  value={empresaForm.actividad_principal}
                  onChange={(e) => setEmpresaForm({ ...empresaForm, actividad_principal: e.target.value })}
                  disabled={isReadOnly}
                  placeholder="Descripción de la actividad"
                />
              </div>

              {/* Datos del Representante Legal */}
              {request?.representante_padron && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <Users size={18} />
                    Representante Legal
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Nombre</p>
                      <p className="font-medium text-slate-900">
                        {request.representante_padron.nombre} {request.representante_padron.apellido || ''}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">CUIT/DNI</p>
                      <p className="font-medium text-slate-900">
                        {request.representante_padron.cuit || request.representante_padron.dni || request.representante_cuit || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Email</p>
                      <p className="font-medium text-slate-900">{request.representante_padron.mail || request.representante_email}</p>
                    </div>
                    {request.representante_padron.telefono && (
                      <div>
                        <p className="text-slate-500">Teléfono</p>
                        <p className="font-medium text-slate-900">{request.representante_padron.telefono}</p>
                      </div>
                    )}
                    {request.representante_padron.domicilio && (
                      <div className="col-span-2">
                        <p className="text-slate-500">Domicilio</p>
                        <p className="font-medium text-slate-900">
                          {request.representante_padron.domicilio}
                          {request.representante_padron.localidad && `, ${request.representante_padron.localidad}`}
                          {request.representante_padron.provincia && ` - ${request.representante_padron.provincia}`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Si no hay datos del padrón, mostrar los datos básicos del representante */}
              {!request?.representante_padron && request?.representante_nombre && (
                <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Users size={18} />
                    Representante Legal
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Nombre</p>
                      <p className="font-medium text-slate-900">{request.representante_nombre}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">CUIT</p>
                      <p className="font-medium text-slate-900">{request.representante_cuit || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Email</p>
                      <p className="font-medium text-slate-900">{request.representante_email}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {!isReadOnly && (
                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={handleSaveEmpresa} disabled={saving}>
                    {saving ? 'Guardando...' : 'Guardar'}
                  </Button>
                  <Button onClick={() => { handleSaveEmpresa(); setCurrentStep(1); }}>
                    Siguiente <ArrowRight className="ml-2" size={16} />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText size={20} className="text-blue-600" />
                Documentación
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-500 mb-6">
                Suba los documentos requeridos. Formatos permitidos: PDF, JPG, PNG. Máximo {portalInfo?.max_file_size_mb || 25}MB por archivo.
              </p>
              
              <div className="space-y-4">
                {request?.documentos_requeridos?.map(doc => {
                  const uploadedDoc = request.documentos?.find(d => d.tipo_documento_id === doc.id);
                  
                  return (
                    <div 
                      key={doc.id}
                      className={`p-4 border rounded-lg ${doc.subido ? 'border-green-200 bg-green-50' : 'border-slate-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {doc.subido ? (
                            <CheckCircle className="text-green-600" size={20} />
                          ) : (
                            <FileText className="text-slate-400" size={20} />
                          )}
                          <div>
                            <p className="font-medium text-slate-900">{doc.nombre}</p>
                            {uploadedDoc && (
                              <p className="text-sm text-slate-500">{uploadedDoc.filename}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {uploadedDoc && !isReadOnly && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDocument(uploadedDoc.id)}
                            >
                              <Trash2 size={16} className="text-red-500" />
                            </Button>
                          )}
                          
                          {!doc.subido && !isReadOnly && (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  if (e.target.files?.[0]) {
                                    handleUploadDocument(doc.id, e.target.files[0]);
                                  }
                                }}
                                disabled={uploading}
                              />
                              <Button variant="outline" size="sm" disabled={uploading} asChild>
                                <span>
                                  <Upload size={16} className="mr-2" />
                                  {uploading ? 'Subiendo...' : 'Subir'}
                                </span>
                              </Button>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {!isReadOnly && (
                <div className="flex justify-between pt-6">
                  <Button variant="outline" onClick={() => setCurrentStep(0)}>
                    <ArrowLeft className="mr-2" size={16} /> Anterior
                  </Button>
                  <Button onClick={() => setCurrentStep(2)}>
                    Siguiente <ArrowRight className="ml-2" size={16} />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {currentStep === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send size={20} className="text-blue-600" />
                Revisión y Envío
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Resumen */}
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Datos de la Empresa</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p><span className="text-slate-500">Razón Social:</span> {empresaForm.razon_social}</p>
                    <p><span className="text-slate-500">CUIT:</span> {empresaForm.cuit}</p>
                    <p><span className="text-slate-500">Domicilio:</span> {empresaForm.domicilio}</p>
                    <p><span className="text-slate-500">Localidad:</span> {empresaForm.localidad}, {empresaForm.provincia}</p>
                  </div>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Documentos</h4>
                  <div className="space-y-1">
                    {request?.documentos_requeridos?.map(doc => (
                      <div key={doc.id} className="flex items-center gap-2 text-sm">
                        {doc.subido ? (
                          <CheckCircle size={14} className="text-green-600" />
                        ) : (
                          <XCircle size={14} className="text-red-500" />
                        )}
                        <span>{doc.nombre}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Validación */}
              {!request?.progreso?.datos_empresa || !request?.progreso?.documentos ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-700 font-medium flex items-center gap-2">
                    <AlertTriangle size={16} />
                    Faltan completar algunos datos:
                  </p>
                  <ul className="list-disc list-inside text-sm text-amber-600 mt-2">
                    {!request?.progreso?.datos_empresa && <li>Datos de la empresa</li>}
                    {!request?.progreso?.documentos && <li>Documentos requeridos</li>}
                  </ul>
                </div>
              ) : !isReadOnly ? (
                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setCurrentStep(1)}>
                    <ArrowLeft className="mr-2" size={16} /> Anterior
                  </Button>
                  <Button onClick={() => setShowSubmitDialog(true)} className="bg-green-600 hover:bg-green-700 text-white">
                    <Send className="mr-2" size={16} /> Enviar a Revisión
                  </Button>
                </div>
              ) : (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                  <CheckCircle className="mx-auto text-green-600 mb-2" size={32} />
                  <p className="text-green-700 font-medium">Solicitud enviada correctamente</p>
                  <p className="text-sm text-green-600">Le notificaremos el resultado por email</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Comentarios */}
        {request?.comentarios?.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare size={20} className="text-blue-600" />
                Mensajes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {request.comentarios.filter(c => !c.is_internal).map(comment => (
                  <div key={comment.id} className={`p-3 rounded-lg ${
                    comment.user_id === 'client' ? 'bg-blue-50 ml-8' : 'bg-slate-50 mr-8'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-slate-700">{comment.user_name}</span>
                      <span className="text-xs text-slate-400">
                        {new Date(comment.created_at).toLocaleDateString('es-AR')}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700">{comment.message}</p>
                  </div>
                ))}
              </div>
              
              {!isReadOnly && (
                <div className="flex gap-2 mt-4">
                  <Input
                    placeholder="Escribir mensaje..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                  />
                  <Button onClick={handleAddComment}>Enviar</Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Submit Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Envío</DialogTitle>
            <DialogDescription>
              ¿Está seguro de enviar la solicitud a revisión? Una vez enviada, no podrá modificar los datos hasta que nuestro equipo la revise.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="font-medium text-slate-900">{empresaForm.razon_social}</p>
              <p className="text-sm text-slate-600">CUIT: {empresaForm.cuit}</p>
              <p className="text-sm text-slate-500 mt-2">
                Domicilio: {empresaForm.domicilio}, {empresaForm.localidad}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} className="bg-green-600 hover:bg-green-700 text-white">
              <Send className="mr-2" size={16} /> Confirmar Envío
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
